import AGGridComponent from 'components/AgGrid/AGGridComponent';
import { dateTimeValueFormatter } from 'components/AgGrid/gridComponents/helpers';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { Box, Tooltip, Typography } from '@mui/material';
import { getServiceAgreement } from 'thunks/accountServiceAgreementsThunk';
import { AppDispatch } from 'store';
import { humanReadableString } from 'utils/stringUtils';
import ApproveIcon from 'assets/icons/agreement-status/approved-status-icon.svg?react';
import Inactive from 'assets/icons/agreement-status/inactive-status-icon.svg?react';
import InitiatedIcon from 'assets/icons/agreement-status/initiated-status-icon.svg?react';
import { DividerTitle } from 'components/common/DividerTitle';
import { determineStatusFromEnum } from 'types';
import { setSelectedServiceAgreement } from 'reducers/serviceAgreementsReducer';
import { setLoader } from 'reducers/appConfigReducer';
import { ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import { ICellRendererParams } from 'ag-grid-community';

const skeletonStyles = {
  display: 'flex',
  height: '60vh',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};

type ProductAndServicesTableProps = {
  itemsToDisplay: { family: string; tableRows: ServiceAgreementV2[] }[] | null;
  partnerId?: string | null;
  brUid?: string | null;
  selectedItem?: ServiceAgreementV2 | null;
  isLoading?: boolean;
  filter?: string;
};

const ProductAndServicesTable = ({
  itemsToDisplay,
  partnerId,
  brUid,
  selectedItem,
  isLoading,
  filter,
}: ProductAndServicesTableProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const handleOldPartnerFlowRedirection = useCallback(
    (data: ServiceAgreementV2) => {
      if (data.serviceDefinitionName === 'JB Mobile Insights') {
        navigate(`/partner/${partnerId}/memberCode`);
      } else {
        navigate(`/partner/${partnerId}/serviceAgreement/${data.serviceAgreementId}/details`);
      }
    },
    [navigate, partnerId],
  );

  const handleRowClick = useCallback(
    (item: { api: { getSelectedRows: () => ServiceAgreementV2[] } }) => {
      const data: ServiceAgreementV2 = item.api.getSelectedRows()[0];

      if (!data.isOldServiceAgreement) {
        dispatch(getServiceAgreement(data.serviceAgreementId, null, false));
      } else {
        dispatch(
          setLoader({
            loading: false,
            message: '',
          }),
        );
        dispatch(setSelectedServiceAgreement(data));
      }

      if (partnerId) {
        handleOldPartnerFlowRedirection(data);
      } else {
        navigate(`/accounts/${brUid}/serviceAgreement/${data.serviceAgreementId}/details`);
      }
    },
    [brUid, dispatch, navigate, partnerId, handleOldPartnerFlowRedirection],
  );

  const renderIconBasedOnStatus = (status: string) => {
    //TODO: if status === "IN_CLOSING" -> return inClosing icon
    switch (determineStatusFromEnum(status)) {
      case 'In Opening':
        return <InitiatedIcon />;
      case 'Active':
        return <ApproveIcon />;
      case 'Inactive':
        return <Inactive />;
    }
  };

  const colDef = [
    {
      field: 'serviceDefinitionName',
      headerName: 'Name',
    },
    {
      field: 'category',
      headerName: 'Category',
    },
    {
      headerName: 'Status',
      field: 'agreementStatus',
      cellRenderer: (params: ICellRendererParams<ServiceAgreementV2>) => {
        const cellValue = params.value.toString();
        const cellWidth = params.column?.getActualWidth() || 0;

        // Create a temporary span element to measure the text width
        const tempSpan = document.createElement('span');
        tempSpan.innerText = cellValue;
        tempSpan.style.visibility = 'hidden';
        document.body.appendChild(tempSpan);

        // Check if the text width is greater than the cell width
        const cellContentWidth = tempSpan.offsetWidth > cellWidth - 30;

        // Remove the temporary span element
        document.body.removeChild(tempSpan);

        return (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              style={{
                display: 'grid',
                paddingRight: '0.5em',
              }}
            >
              {renderIconBasedOnStatus(params.value)}
            </Box>
            <Box>
              {cellContentWidth ? (
                <Tooltip title={humanReadableString(params.value)} arrow>
                  <span>{humanReadableString(params.value)}</span>
                </Tooltip>
              ) : (
                <span>{humanReadableString(params.value)}</span>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'modifiedAt',
      headerName: 'Last Action',
      cellRenderer: (params: ICellRendererParams<ServiceAgreementV2>) => {
        const date = dateTimeValueFormatter({ value: params.value });

        if (!date) return null;

        const cellValue = date.toString();
        const cellWidth = params.column?.getActualWidth() || 0;

        // Create a temporary span element to measure the text width
        const tempSpan = document.createElement('span');
        tempSpan.innerText = cellValue;
        tempSpan.style.visibility = 'hidden';
        document.body.appendChild(tempSpan);

        // Check if the text width is greater than the cell width
        const cellContentWidth = tempSpan.offsetWidth > cellWidth - 30;

        // Remove the temporary span element
        document.body.removeChild(tempSpan);

        if (cellContentWidth) {
          return (
            <Tooltip title={cellValue} arrow>
              <span>{cellValue}</span>
            </Tooltip>
          );
        } else {
          return <span>{cellValue}</span>;
        }
      },
    },
  ];

  const renderNoDataFilterMessage = (filter?: string) => {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6">No {filter?.toLowerCase()} product or service.</Typography>
      </Box>
    );
  };

  return (
    <Box data-testid="products-services-table" sx={{ mr: 3 }}>
      {(itemsToDisplay?.length ?? 0) > 0 ? (
        itemsToDisplay?.map((item: { family: string; tableRows: ServiceAgreementV2[] }, i: number) => {
          return (
            <Box key={i}>
              <>
                <DividerTitle text={item.family} />
                <Box
                  id={`productAndServicesTable-${i}`}
                  sx={{
                    width: '100%',
                    mb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {
                    <AGGridComponent
                      suppressRowHoverHighlight={false}
                      loading={isLoading}
                      selectedItem={(selectedItem as Record<string, unknown> | null) ?? undefined}
                      id={`productAndServicesTable-${i}`}
                      data-testid={`productAndServicesTable-${i}`}
                      onRowClick={handleRowClick}
                      rowKey="serviceAgreementId"
                      getColumnDefs={colDef}
                      items={item.tableRows as unknown as Record<string, unknown>[]}
                    />
                  }
                </Box>
              </>
            </Box>
          );
        })
      ) : (
        <Box sx={skeletonStyles}>{renderNoDataFilterMessage(filter)}</Box>
      )}
    </Box>
  );
};

export default ProductAndServicesTable;
